/** Lifecycle of a purchase request inside the app (per SOW §4). */
export const REQUEST_STATUSES = [
    'DRAFT',
    'PENDING_REVIEW',
    'REJECTED',
    'APPROVED',
    'PR_CREATED',
];
export const PURCHASE_TYPES = ['REGULAR', 'CAPITAL'];
export const AUDIT_ACTIONS = [
    'CREATED',
    'SUBMITTED',
    'APPROVED',
    'REJECTED',
    'RESUBMITTED',
    'PR_CREATED',
    // A D365 creation attempt that failed (#46, wireframe 05.2/V7 and 06.1/A4).
    // Written by the system actor, carrying the raw D365 message as its comment:
    // the attempt history on the failure screen and the "PR failed" row in the
    // admin audit log are the same rows, read from this one trail.
    'PR_FAILED',
    // The requisition was created, but its files could not be linked into D365
    // Document Management (#132). Deliberately **not** `PR_FAILED`: the PR did
    // not fail — it exists, it is in D365's own workflow, and the request is
    // PR_CREATED. Only the attachment sub-step is missing, and the trail has to
    // say so permanently without a red "PR failed" row claiming the opposite or
    // a request with a live requisition turning up in `/pr-failures`.
    'PR_ATTACHMENTS_FAILED',
    // The requisition was created but could not be submitted to D365's workflow
    // (`D365_REQUISITION_SUBMIT_ACTION`). Like `PR_ATTACHMENTS_FAILED` it is not a
    // `PR_FAILED`: the requisition exists, the request is PR_CREATED, and only
    // the move out of Draft is missing — someone can still submit it in D365.
    'PR_SUBMIT_FAILED',
    // The reviewer's explicit "Notify requester" on that screen (V7 note 6 —
    // nothing about a failure notifies anybody automatically).
    'REQUESTER_NOTIFIED',
    'DELETED',
    // The requester pulled a submitted request back out of review (wireframe
    // 08.3, behaviour rule 2; frames R1d, D2). Status returns to DRAFT — this
    // is the only action that moves a request backward through the lifecycle
    // on the requester's own say-so, rather than a reviewer's decision.
    'WITHDRAWN',
    // A reviewer edited a pending request before deciding (reviewer edit mode).
    // The comment lists each change as `Field: old → new`, so the requester and
    // the audit log see exactly what was altered. Status is unchanged.
    'REVIEWER_EDITED',
    // The draft-expiry sweep (#150) removed it automatically, after the
    // configured window of inactivity — a distinct action from the requester's
    // own `DELETED` so the audit trail always shows which one actually happened,
    // written by the system actor (`systemActor.ts`) the same way `PR_FAILED`
    // and `PR_ATTACHMENTS_FAILED` are.
    'DRAFT_EXPIRED',
    // Admin-side actions (#50, #52) — not tied to any single request, hence
    // AuditEntry.requestId being nullable.
    'REVIEWER_ADDED',
    'REVIEWER_REMOVED',
    // Administrator group management (#156, wireframe A1 note 2) — same
    // self-removal / last-one-standing rules as the reviewer group, applied to
    // who may reach the Admin tab at all.
    'ADMIN_ADDED',
    'ADMIN_REMOVED',
    // One row per changed setting, carrying its old and new value (#52).
    'SETTING_CHANGED',
    // An automatic notification (the PR-created email/card, or the rejection
    // notice) that failed to send (#235). Written by the system actor, the same
    // way `PR_FAILED` and `PR_ATTACHMENTS_FAILED` are. Mirrors
    // `PR_ATTACHMENTS_FAILED`'s reasoning applied to notifications instead of
    // attachments: the thing itself — the approval, the rejection, the PR
    // creation — did not fail, only this sub-part did, and the request's own
    // status is untouched. Exists so a live Graph/mailbox failure is visible in
    // the request's History and the admin audit log instead of only in a log
    // line nobody but a server operator can see.
    'NOTIFICATION_FAILED',
];
/**
 * The sub-steps of one D365 PR-creation attempt, in the order they run —
 * wireframe 05.2 / V5, which draws them as four rows ("Header + dimensions",
 * "3 line items", "3 attachments", "Notification") precisely because, in the
 * sheet's own words, *"attachments are the slow part and the most likely to
 * fail on their own"* (V5 note 3).
 *
 * Only the first three can fail the attempt. `NOTIFICATION` is listed because
 * V5 draws it, but it is deliberately not a failure step: the requisition
 * already exists by the time it runs and no notification problem may undo that
 * (see `notifyRequesterOfCreation`).
 */
export const PR_CREATION_STEPS = ['HEADER', 'LINES', 'ATTACHMENTS', 'NOTIFICATION'];
/** V5's own row labels, so the same words appear wherever a step is named. */
export const PR_CREATION_STEP_LABELS = {
    HEADER: 'Header + dimensions',
    LINES: 'Line items',
    ATTACHMENTS: 'Attachments',
    NOTIFICATION: 'Notification',
};
/**
 * A `PR_FAILED` audit comment carries which sub-step failed, as a prefix on
 * the D365 message it already carried.
 *
 * A prefix rather than a column: `AuditEntry` rows are immutable and are
 * already the single trail both V7's attempt history and the admin audit log
 * read (#46), and the message body stays verbatim after the tag is stripped —
 * V7 note 3 is that a D365 administrator must see exactly what D365 said. Rows
 * written before this existed simply parse as "no step", which is why
 * `parsePrFailure` never throws.
 */
const STEP_TAG = /^\[step:(?<step>[A-Z]+)\]\s(?<message>[\s\S]*)$/;
export function formatPrFailure(step, message) {
    return `[step:${step}] ${message}`;
}
export function parsePrFailure(comment) {
    const groups = STEP_TAG.exec(comment)?.groups;
    const step = PR_CREATION_STEPS.find((candidate) => candidate === groups?.step);
    return step ? { step, message: groups?.message ?? '' } : { step: null, message: comment };
}
/**
 * Which part of the request a rejection is about (#155, wireframe 08.3 rule 7 /
 * R8) — the badge R8's step-tab draws next to whichever section the requester
 * needs to fix. `RequestForm.tsx` has no step tabs of its own (see its
 * `focusSection` docblock — "one continuous editing surface instead") but is
 * already divided into exactly these three groupings, each with its own
 * `handleEdit*`/section ref: header + dimension fields, the line items table,
 * and attachments. Kept to those three rather than inventing finer-grained
 * ones the form has no matching anchor for.
 */
export const REJECTION_SECTIONS = ['HEADER', 'LINE_ITEMS', 'ATTACHMENTS'];
/** R8's own words for each section's badge. */
export const REJECTION_SECTION_LABELS = {
    HEADER: 'Request details',
    LINE_ITEMS: 'Line items',
    ATTACHMENTS: 'Attachments',
};
/**
 * A `REJECTED` audit comment optionally carries which section it is about, the
 * same prefix-tag shape `formatPrFailure`/`parsePrFailure` already use for
 * `PR_FAILED` — chosen over a schema column because `AuditEntry` is
 * insert-only (Hard Rule) and this reuses an established, tested pattern
 * rather than a new migration for one optional field. A comment is mandatory
 * on every REJECT, so the tag never appears alone; a comment predating this
 * feature simply has no tag and parses as `section: null`, same as an
 * untagged `PR_FAILED` row does for `parsePrFailure`.
 */
const SECTION_TAG = /^\[section:(?<section>[A-Z_]+)\]\s(?<comment>[\s\S]*)$/;
export function formatRejectionSection(section, comment) {
    return `[section:${section}] ${comment}`;
}
export function parseRejectionSection(comment) {
    const groups = SECTION_TAG.exec(comment)?.groups;
    const section = REJECTION_SECTIONS.find((candidate) => candidate === groups?.section);
    return section ? { section, comment: groups?.comment ?? '' } : { section: null, comment };
}
